/**
 * 講師フィードバック録音用Composable
 * 生徒のチャンク録音システムをベースにフィードバック用に特化
 */

import { ref, reactive, nextTick } from 'vue'
import { ReliableRecorder } from '../utils/reliableRecorder.js'
import { IndexedDBStorage } from '../utils/indexedDBStorage.js'
import { supabase } from '../supabaseClient'
import { loadLocalRotation, saveLocalRotation } from '../utils/videoRecorder.js'
import { getRotationPreference, setRotationPreference } from './useInstructorSettings.js'

export function useFeedbackRecorder() {
  // 録音状態
  const isRecording = ref(false)
  const isUploading = ref(false)
  const isCompleted = ref(false) // 録音完了状態
  const canStopRecording = ref(false) // 録音停止ボタンの有効/無効
  const isDeleting = ref(false) // 削除中状態
  const recordingTime = ref(0)
  const uploadProgress = reactive({
    total: 0,
    completed: 0,
    percentage: 0
  })
  const finalAudioUrl = ref(null) // 結合済み音声のURL
  const previewStream = ref(null) // 動画録画中のライブプレビュー用（自撮り確認、音声では未使用。生カメラstreamそのもの）

  // 動画: 録画開始前のプレビュー確認・向き補正フェーズ用の状態
  const isPreviewReady = ref(false) // カメラ取得完了（まだ録画は開始していない）
  const previewRotationDeg = ref(0) // 現在の回転補正値（0/90/180/270）
  const previewSourceInfo = ref(null) // 生カメラの解像度など（デバッグ・初期値決定用）
  let previewRawStream = null // 回転補正値の端末ローカル保存キー導出用（生カメラstream、stopはsession側が担当）

  // 録音器とタイマー
  let reliableRecorder = null
  let recordingTimer = null

  // 現在の録音情報
  const currentRecording = ref({
    instructorId: null,
    studentId: null,
    submissionId: null,
    day: null,
    type: null
  })

  /**
   * 動画プレビュー準備（録画ボタンを押す前に呼ぶ）。
   * カメラを取得してプレビュー表示できる状態にする。向きの補正はCSS表示上だけで行い、
   * 実際の録画データ(生ストリーム)には一切手を加えない。この時点ではまだMediaRecorderは起動しない。
   */
  const prepareVideoPreview = async (submissionData) => {
    try {
      console.log('🎬 動画プレビュー準備開始:', submissionData)

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error('認証が必要です')
      }

      currentRecording.value = {
        instructorId: user.id,
        studentId: submissionData.user_id,
        submissionId: submissionData.id,
        day: submissionData.day,
        type: submissionData.type || 'practice',
        recordingId: null
      }

      reliableRecorder = new ReliableRecorder({
        maxRecordingTime: 5 * 60 * 1000,
        autoUpload: true,
        keepChunksForPlayback: true,
        onStart: handleRecordingStart,
        onProgress: handleProgress,
        onChunk: handleChunk,
        onComplete: handleRecordingComplete,
        onError: handleError,
        onStreamReady: (stream) => { previewStream.value = stream }
      })

      // アカウントに保存されている補正値を初期値の第一候補にする（新しい端末で開いた場合など）
      const accountDefault = await getRotationPreference()

      const session = await reliableRecorder.prepareVideoPreview({
        day: currentRecording.value.day,
        type: 'feedback',
        userId: currentRecording.value.instructorId,
        studentId: currentRecording.value.studentId,
        submissionId: currentRecording.value.submissionId,
        recordingMode: 'feedback'
      }, accountDefault)

      previewRawStream = session.rawStream

      // この端末・このカメラ解像度で以前に補正した値があれば、そちらを優先する
      const localOverride = loadLocalRotation(previewRawStream)
      if (localOverride !== null && localOverride !== accountDefault) {
        session.setRotation(localOverride)
      }

      previewRotationDeg.value = session.getRotation()
      previewSourceInfo.value = session.sourceInfo
      isPreviewReady.value = true

      console.log('✅ 動画プレビュー準備完了:', session.sourceInfo, { rotationDeg: previewRotationDeg.value })
    } catch (error) {
      console.error('❌ 動画プレビュー準備エラー:', error)
      isPreviewReady.value = false
      previewStream.value = null
      alert(`カメラの準備に失敗しました: ${error.message}`)
      throw error
    }
  }

  /**
   * プレビュー中の向きを時計回りに90度回転する（「向きを直す」ボタン用）
   * 端末ローカル保存＋アカウントへの保存（次回以降の初期値用）を両方行う
   */
  const rotatePreview = () => {
    if (!reliableRecorder || !isPreviewReady.value) return
    const deg = reliableRecorder.rotatePreviewClockwise()
    previewRotationDeg.value = deg
    if (previewRawStream) {
      saveLocalRotation(previewRawStream, deg)
    }
    // アカウントへの保存は失敗してもUIをブロックしない（fire-and-forget）
    setRotationPreference(deg).catch(() => {})
  }

  /**
   * プレビュー確認をキャンセルする（録画を開始せずカメラを解放する）
   */
  const cancelVideoPreview = () => {
    if (reliableRecorder) {
      reliableRecorder.cancelPreview()
    }
    previewStream.value = null
    previewSourceInfo.value = null
    previewRotationDeg.value = 0
    previewRawStream = null
    isPreviewReady.value = false
  }
  
  /**
   * 録音開始
   */
  const startRecording = async (submissionData, mediaKind = 'audio') => {
    try {
      console.log('🎤 フィードバック録音開始:', submissionData, { mediaKind, isPreviewReady: isPreviewReady.value })

      // 動画で既にprepareVideoPreview()済みなら、そのカメラセッションと
      // reliableRecorderインスタンスをそのまま使う（プレビューで確認した内容＝録画内容にするため）
      const reuseExistingPreview = mediaKind === 'video' && isPreviewReady.value && reliableRecorder

      if (!reuseExistingPreview) {
        // 認証状態確認
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) {
          throw new Error('認証が必要です')
        }

        // 録音情報設定
        currentRecording.value = {
          instructorId: user.id,
          studentId: submissionData.user_id,
          submissionId: submissionData.id,
          day: submissionData.day,
          type: submissionData.type || 'practice',
          recordingId: null // 録音開始後に設定される
        }

        // ReliableRecorder初期化（フィードバック用設定：IndexedDBにチャンクを残して自己確認再生用）
        // 動画は最大5分（ハード上限）、音声は既存どおり20分
        reliableRecorder = new ReliableRecorder({
          maxRecordingTime: mediaKind === 'video' ? 5 * 60 * 1000 : 1200000,
          autoUpload: true,
          keepChunksForPlayback: true,
          onStart: handleRecordingStart,
          onProgress: handleProgress,
          onChunk: handleChunk,
          onComplete: handleRecordingComplete,
          onError: handleError,
          onStreamReady: (stream) => { previewStream.value = stream }
        })
      }

      // 録音開始（フィードバック用パラメータ）
      await reliableRecorder.startRecording({
        day: currentRecording.value.day,
        type: 'feedback', // フィードバック用に固定
        userId: currentRecording.value.instructorId, // 録音者は講師
        // フィードバック固有パラメータ
        studentId: currentRecording.value.studentId,
        submissionId: currentRecording.value.submissionId,
        recordingMode: 'feedback', // フィードバックモードフラグ
        mediaKind // 'audio'（デフォルト）または 'video'
      })

      isPreviewReady.value = false // 録画が始まったのでプレビュー準備状態はリセット

      // UI状態更新
      isRecording.value = true
      canStopRecording.value = false // 最初は停止不可
      recordingTime.value = 0
      uploadProgress.total = 0
      uploadProgress.completed = 0
      uploadProgress.percentage = 0
      
      // 録音時間カウンター開始
      startRecordingTimer()
      
      // 3秒後に停止ボタンを有効化
      setTimeout(() => {
        canStopRecording.value = true
        console.log('✅ 録音停止ボタンが有効になりました')
      }, 3000)
      
      // recordingIdを保存
      if (reliableRecorder.recordingId) {
        currentRecording.value.recordingId = reliableRecorder.recordingId
        console.log('📝 recordingID保存:', reliableRecorder.recordingId)
      }
      
      console.log('✅ フィードバック録音開始成功（3秒後に停止可能）')
      
    } catch (error) {
      console.error('❌ フィードバック録音開始エラー:', error)
      handleError(error)
      throw error
    }
  }
  
  /**
   * 録音停止（Practice と同様: UI を先に更新してから Recorder を停止）
   */
  const stopRecording = async () => {
    try {
      console.log('⏹️ フィードバック録音停止ボタン押下')

      // 1. UI状態を即座に更新（Practice と同じ）
      isRecording.value = false
      stopRecordingTimer()
      isUploading.value = true

      // 2. 描画を待ってから Recorder を停止（Practice の nextTick + setTimeout(0) と同様）
      nextTick(() => {
        setTimeout(() => {
          try {
            if (reliableRecorder) {
              reliableRecorder.stopRecording()
            }
            console.log('✅ フィードバック録音停止完了')
          } catch (error) {
            console.error('❌ フィードバック録音停止エラー:', error)
            handleError(error)
          }
        }, 0)
      })
    } catch (error) {
      console.error('❌ フィードバック録音停止エラー:', error)
      handleError(error)
    }
  }
  
  /**
   * 録音キャンセル
   */
  const cancelRecording = async () => {
    try {
      console.log('❌ フィードバック録音キャンセル')
      
      // 録音停止
      isRecording.value = false
      isUploading.value = false
      stopRecordingTimer()
      
      // 録音器クリーンアップ
      if (reliableRecorder) {
        await reliableRecorder.stopRecording()
        reliableRecorder = null
      }
      
      // 状態リセット
      resetState()
      
      console.log('✅ フィードバック録音キャンセル完了')
      
    } catch (error) {
      console.error('❌ フィードバック録音キャンセルエラー:', error)
    }
  }
  
  /**
   * 録音時間タイマー開始
   */
  const startRecordingTimer = () => {
    recordingTimer = setInterval(() => {
      recordingTime.value += 1
    }, 1000)
  }
  
  /**
   * 録音時間タイマー停止
   */
  const stopRecordingTimer = () => {
    if (recordingTimer) {
      clearInterval(recordingTimer)
      recordingTimer = null
    }
  }
  
  /**
   * 時間フォーマット
   */
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  
  /**
   * フィードバック音声URLを取得（処理中の場合はリトライ）
   */
  const getFeedbackAudioUrl = async (recordingId, retries = 5) => {
    const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token || ''

    for (let attempt = 1; attempt <= retries; attempt++) {
      const response = await fetch(`${apiBase}/api/feedback-audio-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ recordingId })
      })

      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        return data.finalUrl || null
      }
      // 処理中（worker未完了）の場合は待ってリトライ
      if (response.status === 202 && attempt < retries) {
        console.log(`⏳ 音声処理中、リトライ ${attempt}/${retries}...`)
        await new Promise(r => setTimeout(r, 2000))
        continue
      }
      const msg = data.error || data.message || `API Error: ${response.status}`
      throw new Error(msg)
    }
    return null
  }
  
  /**
   * フィードバック録音データをR2から削除
   */
  const deleteFeedbackRecording = async (recordingId) => {
    try {
      // 認証トークン取得
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token || ''
      
      // バックエンドでメモリから finalKey を取得するため、recordingId のみ送信
      const requestBody = { recordingId }
      console.log('🔍 削除API呼び出し:', {
        url: `${import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'}/api/delete-feedback-recording`,
        body: requestBody
      })
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'}/api/delete-feedback-recording`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      })
      
      console.log('🔍 削除APIレスポンス:', {
        status: response.status,
        ok: response.ok
      })
      
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        const msg = data.error || data.message || `削除API Error: ${response.status}`
        throw new Error(msg)
      }
      console.log('✅ フィードバック録音削除API成功:', data)
      return data
    } catch (error) {
      console.error('❌ フィードバック録音削除API エラー:', error)
      throw error
    }
  }
  
  /**
   * 撮り直し（データ破棄して再録音）
   */
  const retakeRecording = async () => {
    const recordingId = currentRecording.value.recordingId
    try {
      console.log('🔄 フィードバック録音の撮り直し開始')

      isDeleting.value = true

      if (recordingId) {
        console.log('🗑️ R2からフィードバック録音データを削除中...')
        await deleteFeedbackRecording(recordingId).catch(console.warn)
        console.log('✅ R2削除完了')
      }

      await cancelRecording()

      isCompleted.value = false
      finalAudioUrl.value = null
      isDeleting.value = false

      await cleanupRecordingData(recordingId)
      console.log('✅ 撮り直し準備完了')
    } catch (error) {
      console.error('❌ 撮り直しエラー:', error)
      isDeleting.value = false
      alert(`撮り直しエラー: ${error.message}`)
    }
  }
  
  /**
   * LINE送信（フィードバック確定）
   */
  const sendToLine = async () => {
    if (!finalAudioUrl.value) {
      console.error('❌ 送信可能な音声がありません')
      alert('送信可能な音声がありません')
      throw new Error('送信可能な音声がありません')
    }
    
    if (!currentRecording.value.recordingId) {
      console.error('❌ recordingIdが設定されていません')
      alert('録音IDが見つかりません')
      throw new Error('recordingIdが設定されていません')
    }
    
    try {
      console.log('📤 フィードバック送信開始:', currentRecording.value)
      
      // 認証トークン取得
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token || ''
      
      // バックエンドAPI呼び出し
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'}/api/submit-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          recordingId: currentRecording.value.recordingId,
          submissionId: currentRecording.value.submissionId,
          studentId: currentRecording.value.studentId,
          day: currentRecording.value.day,
          type: currentRecording.value.type,
          duration: recordingTime.value
        })
      })
      
      const result = await response.json().catch(() => ({}))
      if (!response.ok) {
        const msg = result.message || result.error || `送信に失敗しました (${response.status})`
        throw new Error(msg)
      }
      
      console.log('✅ フィードバック送信成功:', result)
      await cleanupRecordingData(currentRecording.value.recordingId)
      return result
    } catch (error) {
      console.error('❌ フィードバック送信エラー:', error)
      throw error
    }
  }
  
  /**
   * 状態リセット
   */
  const resetState = () => {
    recordingTime.value = 0
    uploadProgress.total = 0
    uploadProgress.completed = 0
    uploadProgress.percentage = 0
    isCompleted.value = false
    canStopRecording.value = false
    isDeleting.value = false
    finalAudioUrl.value = null
    previewStream.value = null
    isPreviewReady.value = false
    previewRotationDeg.value = 0
    previewSourceInfo.value = null
    previewRawStream = null
    currentRecording.value = {
      instructorId: null,
      studentId: null,
      submissionId: null,
      day: null,
      type: null
    }
  }
  
  // イベントハンドラー
  const handleRecordingStart = () => {
    console.log('🎤 フィードバック録音開始イベント')
  }
  
  const handleProgress = (progress) => {
    // アップロード進捗をUIに反映（Practice の進捗表示と同様）
    if (progress.totalChunks != null) uploadProgress.total = progress.totalChunks
    if (progress.uploadedChunks != null) uploadProgress.completed = progress.uploadedChunks
    if (progress.uploadProgress != null) uploadProgress.percentage = progress.uploadProgress
  }

  const handleChunk = (chunkInfo) => {
    console.log('📦 フィードバックチャンク生成:', chunkInfo)
    uploadProgress.total = chunkInfo.totalChunks ?? uploadProgress.total
  }
  
  /**
   * IndexedDBのチャンクからローカル再生用URLを構築（A方式: 録音時のMIMEでBlob連結）
   */
  const buildLocalPlaybackUrl = async (recordingId) => {
    const storage = new IndexedDBStorage()
    const chunks = await storage.getChunksByRecordingId(recordingId)
    if (!chunks?.length) return null
    const mimeType = chunks[0]?.mimeType || chunks[0]?.type || 'audio/webm'
    const audioBlob = new Blob(chunks.map(c => c.data), { type: mimeType })
    return URL.createObjectURL(audioBlob)
  }

  /**
   * blob: URLを解放（メモリリーク防止）
   */
  const revokeBlobUrl = () => {
    if (finalAudioUrl.value && typeof finalAudioUrl.value === 'string' && finalAudioUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(finalAudioUrl.value)
      finalAudioUrl.value = null
    }
  }

  /**
   * 該当録音のIndexedDBを破棄し、blob URLを解放（送信・撮り直し・モーダル閉じ時）
   */
  const cleanupRecordingData = async (recordingId) => {
    if (!recordingId) return
    revokeBlobUrl()
    const storage = new IndexedDBStorage()
    await storage.cleanupRecording(recordingId)
    console.log('🗑️ IndexedDBクリーンアップ完了:', recordingId)
  }

  const handleRecordingComplete = async (result) => {
    try {
      console.log('✅ フィードバック録音完了:', result)

      isUploading.value = true
      const recordingId = result.recordingId
      console.log('🔍 録音完了結果:', result)

      // D=0: 遅延なし。先にローカル再生用URLを構築してから「完了」表示にすることで、プレイヤーが空の一瞬を避ける
      let localUrl = null
      try {
        localUrl = await buildLocalPlaybackUrl(recordingId)
        if (localUrl) {
          revokeBlobUrl()
          finalAudioUrl.value = localUrl
          console.log('🎵 ローカル確認用音声URL設定成功（IndexedDB連結）')
        }
      } catch (err) {
        console.warn('⚠️ ローカル再生URL構築失敗:', err)
      }

      isUploading.value = false
      isCompleted.value = true
      if (!localUrl) {
        console.log('✅ フィードバック録音処理完了 - UI準備完了（再生URLなし）')
      }
    } catch (error) {
      console.error('❌ フィードバック録音完了処理エラー:', error)
      handleError(error)
    }
  }
  
  const handleError = (error) => {
    console.error('❌ フィードバック録音エラー:', error)
    
    // UI状態リセット
    isRecording.value = false
    isUploading.value = false
    canStopRecording.value = false
    isDeleting.value = false
    isCompleted.value = false
    isPreviewReady.value = false
    revokeBlobUrl()
    stopRecordingTimer()

    // エラー通知
    const msg = error?.message || String(error || '')
    if (msg.includes('QuotaExceeded')) {
      alert('端末のストレージ容量が不足しているため、録音データを保存できませんでした。\n写真や動画、使っていないアプリなどを削除して、少なくとも 100MB 以上の空き容量を確保してから、もう一度録音をお試しください。')
    } else {
      alert(`録音エラーが発生しました: ${msg}`)
    }
  }
  
  // クリーンアップ
  const cleanup = () => {
    revokeBlobUrl()
    stopRecordingTimer()
    if (reliableRecorder) {
      const recorder = reliableRecorder
      reliableRecorder = null
      if (isRecording.value) {
        Promise.resolve(recorder.stopRecording()).catch(console.error)
      } else {
        // 録画未開始（プレビュー確認中）ならカメラセッションを解放する
        recorder.destroy()
      }
    }
    previewStream.value = null
    isPreviewReady.value = false
  }

  return {
    // 状態
    isRecording,
    isUploading,
    isCompleted,
    canStopRecording,
    isDeleting,
    recordingTime,
    uploadProgress,
    currentRecording,
    finalAudioUrl,
    previewStream,
    isPreviewReady,
    previewRotationDeg,
    previewSourceInfo,

    // メソッド
    prepareVideoPreview,
    rotatePreview,
    cancelVideoPreview,
    startRecording,
    stopRecording,
    cancelRecording,
    retakeRecording,
    sendToLine,
    formatTime,
    resetState,
    cleanup,
    cleanupRecordingData,
    deleteFeedbackRecording
  }
}
